/**
 * ENG-9048 AC3 — the Slack inbound-transport liveness signal, shared by the
 * process that knows it and the process that can act on it.
 *
 * WHY A FILE
 *
 * `slack-channel` knows whether Socket Mode is carrying inbound; only the
 * manager can restart an MCP child. They are separate processes, and nothing
 * connected them. During the ENG-9048 outage the channel knew the socket had
 * been dead for 3h47m and had no way to say so to anything that could act, so
 * an operator-approved session restart was the only recovery.
 *
 * This is the smallest seam that closes that: the channel writes its socket
 * state to a file in the agent dir, the manager reads it on the poll it
 * already runs. The `channel-progress-heartbeat.json` precedent in the same
 * directory does exactly this shape already.
 *
 * WHY THE STALENESS RULE IS THE LOAD-BEARING PART
 *
 * The consumer of this signal is `session-tool-rebind`, which SIGTERMs an MCP
 * child. That flag is enabled globally, so a false "down" costs a real restart
 * on a healthy agent — and a stuck or crashed writer produces a file that is
 * frozen at its last value, which is exactly when a naive reader would report
 * the stale contents with total confidence.
 *
 * So the rule is: **an old file says nothing.** Not "still down", not "now up".
 * `readSlackSocketState` returns `null` for missing, unparseable, malformed and
 * stale files alike, and every caller must treat `null` as "no verdict this
 * cycle" rather than as either state. That matches how the probe host already
 * handles a failed `ps`: bail rather than risk acting on absent evidence.
 */

/** Filename inside the agent directory. Stable — both processes resolve it. */
export const SLACK_SOCKET_STATE_FILENAME = 'slack-socket-state.json';

/**
 * How old the file may be and still be believed.
 *
 * The writer refreshes on every state change AND on a heartbeat well inside
 * this window, so a file older than this means the writer itself is gone or
 * wedged — a condition this signal is not entitled to have an opinion about.
 * Deliberately generous relative to the write cadence: the cost of ignoring a
 * real outage for one more cycle is one cycle, and the cost of acting on a
 * frozen file is restarting a healthy agent's MCP child.
 */
export const SLACK_SOCKET_STATE_MAX_AGE_MS = 180_000;

/** What the channel writes. */
export interface SlackSocketState {
  /** True iff the WebSocket is OPEN and carrying inbound right now. */
  connected: boolean;
  /**
   * How long inbound has been dead, in ms, or null when it is up. Written by
   * the channel from its own clock — the reader does not recompute it, because
   * the two processes' clocks are the same host clock but the writer is the
   * only one that knows when the socket actually dropped.
   */
  down_for_ms: number | null;
  /** Writer's clock at write time (epoch ms). Drives the staleness gate. */
  updated_at_ms: number;
}

/** Serialize for the writer. Kept here so both sides agree on the shape. */
export function serializeSlackSocketState(state: SlackSocketState): string {
  return JSON.stringify(state);
}

/**
 * Parse + validate + freshness-gate. Returns null on ANY doubt.
 *
 * `nowMs` is injected rather than read from the clock so the staleness rule is
 * testable without sleeping — a rule that cannot be tested cheaply is a rule
 * that silently stops holding.
 */
export function parseSlackSocketState(
  raw: string | null | undefined,
  nowMs: number,
  maxAgeMs: number = SLACK_SOCKET_STATE_MAX_AGE_MS,
): SlackSocketState | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const o = parsed as Record<string, unknown>;

  if (typeof o['connected'] !== 'boolean') return null;
  if (typeof o['updated_at_ms'] !== 'number' || !Number.isFinite(o['updated_at_ms'])) return null;

  const downRaw = o['down_for_ms'];
  const downForMs =
    downRaw === null
      ? null
      : typeof downRaw === 'number' && Number.isFinite(downRaw) && downRaw >= 0
        ? downRaw
        : undefined;
  if (downForMs === undefined) return null;

  const age = nowMs - (o['updated_at_ms'] as number);
  // Stale in the past: the writer is gone or wedged, so it gets no vote.
  if (age > maxAgeMs) return null;
  // Stale in the FUTURE: a clock jump or a hand-edited file. Same treatment —
  // this is not evidence, and an unbounded future timestamp would otherwise
  // read as permanently fresh.
  if (age < -maxAgeMs) return null;

  return {
    connected: o['connected'] as boolean,
    down_for_ms: downForMs,
    updated_at_ms: o['updated_at_ms'] as number,
  };
}

/**
 * Is the Slack inbound transport down for long enough to act on?
 *
 * Every uncertain input answers FALSE. This is the predicate that decides
 * whether an MCP child gets SIGTERMed, so the only input that may return true
 * is a fresh file that positively says the socket is down and has been for
 * longer than the threshold. "Down but only briefly" is ordinary churn — one
 * agent logged 84 disconnects in a week — and must not restart anything.
 */
export function isSlackInboundTransportDown(
  state: SlackSocketState | null,
  downForAtLeastMs: number,
): boolean {
  if (!state) return false;
  if (state.connected) return false;
  if (state.down_for_ms === null) return false;
  return state.down_for_ms >= downForAtLeastMs;
}
